import { useEffect, useState } from "react";
import axios from "axios";
import { format, addDays, parseISO } from "date-fns";
import { BarChart3, Loader2, Utensils, Wine, ReceiptText, ChevronLeft, ChevronRight, CalendarDays, Printer } from "lucide-react";
import { printSalesRecap } from "./receipt";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const todayStr = () => format(new Date(), "yyyy-MM-dd");

export default function SalesPage() {
  const [date, setDate] = useState(todayStr());
  const [data, setData] = useState(null);
  const [week, setWeek] = useState(null);

  useEffect(() => {
    setData(null);
    axios.get(`${API}/orders/summary`, { params: { date } })
      .then((r) => setData(r.data))
      .catch(() => setData({ count: 0, total: 0, by_station: {}, by_category: [], by_spot: [] }));
    const start = format(addDays(parseISO(date), -6), "yyyy-MM-dd");
    axios.get(`${API}/orders/summary/range`, { params: { start, end: date } })
      .then((r) => setWeek(r.data)).catch(() => setWeek(null));
  }, [date]);

  const shift = (n) => { const d = format(addDays(parseISO(date), n), "yyyy-MM-dd"); if (d <= todayStr()) setDate(d); };
  const food = data?.by_station?.kitchen || 0;
  const bar = data?.by_station?.bar || 0;
  const max = Math.max(food, bar, 1);
  const weekMax = Math.max(...((week?.days || []).map((d) => d.total)), 1);

  const Bar = ({ label, val, Icon, color }) => (
    <div>
      <div className="mb-1 flex items-center justify-between text-sm">
        <span className={`flex items-center gap-1.5 font-semibold ${color}`}><Icon size={15} /> {label}</span>
        <span className="font-bold text-slate-800">{val.toFixed(2)}€</span>
      </div>
      <div className="h-2.5 overflow-hidden rounded-full bg-sand-200">
        <div className={`h-full rounded-full ${label === "Food" ? "bg-amber-500" : "bg-sea"}`} style={{ width: `${(val / max) * 100}%` }} />
      </div>
    </div>
  );

  return (
    <div className="mx-auto max-w-3xl p-4">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 font-display text-2xl tracking-wide text-slate-900"><BarChart3 size={22} className="text-brand-700" /> Sales</h2>
        <div className="flex items-center gap-2">
          <button data-testid="sales-recap-btn" onClick={() => data && printSalesRecap(date, data, week)} disabled={!data}
            className="flex h-9 items-center gap-1.5 rounded-full bg-brand px-3 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-50">
            <Printer size={15} /> End-of-day recap
          </button>
          <div className="flex items-center gap-1 rounded-full bg-white p-1 ring-1 ring-sand-300">
          <button data-testid="sales-prev-day" onClick={() => shift(-1)} className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-sand-100"><ChevronLeft size={16} /></button>
          <label className="flex items-center gap-1.5 px-1 text-sm font-semibold text-slate-700">
            <CalendarDays size={14} className="text-brand-700" />
            <input data-testid="sales-date" type="date" value={date} max={todayStr()} onChange={(e) => e.target.value && setDate(e.target.value)}
              className="bg-transparent outline-none" />
          </label>
          <button data-testid="sales-next-day" onClick={() => shift(1)} disabled={date >= todayStr()}
            className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-sand-100 disabled:opacity-30"><ChevronRight size={16} /></button>
          </div>
        </div>
      </div>

      {!data ? <div className="flex justify-center py-20 text-slate-400"><Loader2 className="animate-spin" /></div> : (
        <>
          <div className="mb-4 grid grid-cols-2 gap-3">
            <div className="rounded-2xl border border-sand-300 bg-white p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Revenue</p>
              <p className="mt-1 font-display text-3xl text-slate-900" data-testid="sales-total">{data.total.toFixed(2)}€</p>
            </div>
            <div className="rounded-2xl border border-sand-300 bg-white p-4">
              <p className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-slate-400"><ReceiptText size={12} /> Orders</p>
              <p className="mt-1 font-display text-3xl text-slate-900" data-testid="sales-count">{data.count}</p>
            </div>
          </div>

          <div className="mb-4 space-y-3 rounded-2xl border border-sand-300 bg-white p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-brand-700">Food vs Bar</p>
            <Bar label="Food" val={food} Icon={Utensils} color="text-amber-600" />
            <Bar label="Drinks" val={bar} Icon={Wine} color="text-sea" />
          </div>

          {(data.by_payment || []).length > 0 && (
            <div className="mb-4 rounded-2xl border border-sand-300 bg-white p-4" data-testid="sales-payments">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-brand-700">By payment method</p>
              <div className="grid grid-cols-3 gap-2">
                {data.by_payment.map((p) => (
                  <div key={p.name} className="rounded-xl bg-sand-100 p-2.5 text-center">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{p.name}</p>
                    <p className="mt-0.5 font-display text-lg text-slate-900">{p.total.toFixed(2)}€</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {week && (
            <div className="mb-4 rounded-2xl border border-sand-300 bg-white p-4" data-testid="sales-week">
              <div className="mb-3 flex items-center justify-between">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-brand-700">Last 7 days</p>
                <p className="text-sm font-bold text-slate-800" data-testid="sales-week-total">{week.total.toFixed(2)}€ · {week.count} orders</p>
              </div>
              <div className="flex items-end justify-between gap-1.5" style={{ height: 90 }}>
                {(week.days || []).map((d) => (
                  <button key={d.date} onClick={() => setDate(d.date)} title={`${d.date}: ${d.total.toFixed(2)}€`}
                    className="flex flex-1 flex-col items-center justify-end gap-1">
                    <div className={`w-full rounded-t ${d.date === date ? "bg-brand" : "bg-sea/50"}`} style={{ height: `${Math.max(4, (d.total / weekMax) * 64)}px` }} />
                    <span className="text-[9px] text-slate-400">{format(parseISO(d.date), "EEE")}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border border-sand-300 bg-white p-4">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-brand-700">Top categories</p>
              {data.by_category.length === 0 ? <p className="text-sm text-slate-400">No sales</p> : (
                <ul className="space-y-1 text-sm" data-testid="sales-categories">
                  {data.by_category.slice(0, 10).map((c) => (
                    <li key={c.name} className="flex justify-between"><span className="text-slate-700">{c.name}</span><span className="font-semibold text-slate-500">{c.total.toFixed(2)}€</span></li>
                  ))}
                </ul>
              )}
            </div>
            <div className="rounded-2xl border border-sand-300 bg-white p-4">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-brand-700">By spot</p>
              {data.by_spot.length === 0 ? <p className="text-sm text-slate-400">No sales</p> : (
                <ul className="space-y-1 text-sm" data-testid="sales-spots">
                  {data.by_spot.map((s) => (
                    <li key={s.name} className="flex justify-between"><span className="text-slate-700">{s.name}</span><span className="font-semibold text-slate-500">{s.total.toFixed(2)}€</span></li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
